import { GlobeHemisphereWest, MapPin, Buildings, House, Question, CheckCircle, XCircle } from "@phosphor-icons/react";

const MODE_ICON = {
  remote: GlobeHemisphereWest,
  hybrid: Buildings,
  onsite: MapPin,
  unknown: Question,
};

// Compact badge for job cards/rows. Honest: unknown scope is shown as such (amber),
// never silently presented as a permissive "remote".
export function RemoteScopeBadge({ summary, className = "" }) {
  if (!summary) return null;
  const Icon = MODE_ICON[summary.mode] || Question;
  const tone = summary.known
    ? "text-slate-600 border-slate-200 bg-slate-50"
    : "text-amber-700 border-amber-200 bg-amber-50";
  return (
    <span
      data-testid="remote-scope-badge"
      title={summary.detail || summary.label}
      className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[11px] font-medium ${tone} ${className}`}
    >
      <Icon weight="fill" className="h-3 w-3" /> {summary.label}
      {!summary.known && <span className="opacity-70">· not confirmed</span>}
    </span>
  );
}

// Larger "Where you can work from" block for the job detail page.
export function RemoteScopePanel({ summary }) {
  if (!summary) return null;
  const Icon = MODE_ICON[summary.mode] || Question;
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-6" data-testid="remote-scope-panel">
      <p className="text-xs uppercase tracking-wide text-slate-500 font-medium mb-2">Where you can work from</p>
      <div className="flex items-center gap-2">
        <Icon weight="fill" className="text-slate-500" size={20} />
        <span className="font-heading font-bold text-slate-900">{summary.label}</span>
      </div>
      {summary.detail && <p className="mt-1 text-sm text-slate-600">{summary.detail}</p>}
      {summary.markers?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="remote-markers">
          {summary.markers.map((m, i) => (
            <span key={i} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-medium text-slate-600">{m}</span>
          ))}
        </div>
      )}
      {summary.allowed_from?.length > 0 && (
        <div className="mt-3">
          <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Permitted from</p>
          <div className="flex flex-wrap gap-1">
            {summary.allowed_from.map((c, i) => (
              <span key={i} className="text-xs bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5 text-slate-600 capitalize">{c}</span>
            ))}
          </div>
        </div>
      )}
      {summary.residence_required && (
        <p className="mt-3 text-sm text-slate-700"><span className="font-medium">Residence required:</span> {summary.residence_required}</p>
      )}
      {!summary.known && (
        <p className="mt-3 text-xs text-amber-700">The exact location rule is not stated in the source, so this stays unknown — verify before applying. We don't assume permission.</p>
      )}
    </div>
  );
}

function TriState({ value, label }) {
  const map = {
    true: { Icon: CheckCircle, cls: "text-emerald-600", text: "Yes" },
    false: { Icon: XCircle, cls: "text-red-600", text: "No" },
  };
  const m = value === true ? map.true : value === false ? map.false : { Icon: Question, cls: "text-slate-400", text: "Not confirmed" };
  const Icon = m.Icon;
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className={`inline-flex items-center gap-1 font-medium ${m.cls}`}><Icon weight="fill" className="h-3.5 w-3.5" /> {m.text}</span>
    </div>
  );
}

// Factual source & freshness evidence — no score, no invented trust label.
export function SourceEvidence({ evidence }) {
  if (!evidence) return null;
  const fmt = (v) => (v ? String(v).slice(0, 10) : null);
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-6" data-testid="source-evidence-panel">
      <p className="text-xs uppercase tracking-wide text-slate-500 font-medium mb-3">Source &amp; freshness</p>
      <div className="space-y-2">
        <Row label="Source" value={evidence.source_label} />
        {evidence.employer_direct && (
          <p className="text-xs text-emerald-700" data-testid="employer-direct-note">Posted directly by the employer.</p>
        )}
        <Row label="First seen" value={fmt(evidence.first_seen) || "Not recorded"} />
        <Row label="Last checked" value={fmt(evidence.last_checked) || "Not recorded"} />
        <TriState label="Still active at source" value={evidence.still_active_at_source} />
        <Row label="Closing date" value={fmt(evidence.closing_date) || "Not stated"} />
      </div>
      <p className="mt-3 text-xs text-slate-400">Factual provenance only — no trust score. Where we can't confirm something, we show it as not confirmed rather than assuming it's active.</p>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-800 text-right">{value}</span>
    </div>
  );
}
